import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import {
  NR_AVG_BUCKETS,
  buildNrCountRows,
  type NrCountRow,
} from '../NotRecommendedStatisticsPage';
import type { CrewStatsNrRow } from '../types';

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function num(n: number) {
  return n === 0 ? '—' : String(n);
}

function TableHead({ showUnknown }: { showUnknown: boolean }) {
  return (
    <table className="ob-count-print-table ob-count-print-table-head">
      <thead>
        <tr>
          <th className="ob-count-col-vessel">Last vessel</th>
          <th className="ob-count-col-nat">Nationality</th>
          {NR_AVG_BUCKETS.map((b) => (
            <th key={b.key} className="ob-count-col-num">
              {b.label}
            </th>
          ))}
          {showUnknown ? <th className="ob-count-col-num">No appraisal</th> : null}
          <th className="ob-count-col-num">Total</th>
        </tr>
      </thead>
    </table>
  );
}

function LineRow({ row, showUnknown }: { row: NrCountRow; showUnknown: boolean }) {
  return (
    <table className="ob-count-print-table ob-count-print-row-table">
      <tbody>
        <tr>
          <td className="ob-count-col-vessel">{row.lastVessel}</td>
          <td className="ob-count-col-nat">{row.nationality}</td>
          {NR_AVG_BUCKETS.map((b) => (
            <td key={b.key} className="ob-count-col-num">
              {num(row[b.key])}
            </td>
          ))}
          {showUnknown ? <td className="ob-count-col-num">{num(row.unknown)}</td> : null}
          <td className="ob-count-col-num ob-count-col-total">{row.total}</td>
        </tr>
      </tbody>
    </table>
  );
}

function TotalsRow({ rows, showUnknown }: { rows: NrCountRow[]; showUnknown: boolean }) {
  const totals = rows.reduce(
    (acc, row) => {
      acc.lt2 += row.lt2;
      acc.a2_3 += row.a2_3;
      acc.a3_4 += row.a3_4;
      acc.a4 += row.a4;
      acc.unknown += row.unknown;
      acc.total += row.total;
      return acc;
    },
    { lt2: 0, a2_3: 0, a3_4: 0, a4: 0, unknown: 0, total: 0 },
  );
  return (
    <table className="ob-count-print-table ob-count-print-totals-table">
      <tbody>
        <tr>
          <td className="ob-count-col-vessel" colSpan={2}>
            Total
          </td>
          {NR_AVG_BUCKETS.map((b) => (
            <td key={b.key} className="ob-count-col-num">
              {totals[b.key]}
            </td>
          ))}
          {showUnknown ? <td className="ob-count-col-num">{totals.unknown}</td> : null}
          <td className="ob-count-col-num ob-count-col-total">{totals.total}</td>
        </tr>
      </tbody>
    </table>
  );
}

export type NrCountReportProps = {
  nr: CrewStatsNrRow[];
  vesselLabel: string;
};

export const NrCountReport: React.FC<NrCountReportProps> = ({ nr, vesselLabel }) => {
  const rows = useMemo(() => buildNrCountRows(nr), [nr]);
  const showUnknown = rows.some((r) => r.unknown > 0);
  const vesselCount = new Set(rows.map((r) => r.lastVessel)).size;
  const nationalityCount = new Set(rows.map((r) => r.nationality)).size;

  const firstHeader = useMemo(
    () => (
      <div className="ob-count-print-flow-header">
        <ReportHeader>
          <ReportTitleRow title="Not recommended — nationality and appraisal" formId="Statistics" />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={vesselLabel} />
              <ReportField label="Crew" value={String(nr.length)} />
              <ReportField label="Nationalities" value={String(nationalityCount)} />
            </ReportMetaRow>
            <ReportMetaRow>
              <ReportField label="Last vessels" value={String(vesselCount)} />
              <ReportField label="Printed" value={fmtDate(new Date().toISOString().slice(0, 10))} />
            </ReportMetaRow>
          </ReportMeta>
        </ReportHeader>
        <TableHead showUnknown={showUnknown} />
      </div>
    ),
    [nationalityCount, nr.length, showUnknown, vesselCount, vesselLabel],
  );

  const continueHeader = useMemo(
    () => (
      <div className="ob-count-print-flow-header">
        <div className="ob-count-print-continue-bar">
          <span className="ob-count-print-continue-title">Not recommended counts</span>
          <span className="ob-count-print-continue-meta">{vesselLabel}</span>
          <span className="ob-count-print-continue-hint">continued</span>
        </div>
        <TableHead showUnknown={showUnknown} />
      </div>
    ),
    [showUnknown, vesselLabel],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportSigns>
          <ReportSignLine label="Prepared by" />
          <ReportSignLine label="Date" />
        </ReportSigns>
        <ReportNote>
          Count of not-recommended crew per last vessel, nationality, and appraisal average
          (questionnaire).
        </ReportNote>
      </ReportFooter>
    ),
    [],
  );

  const { items, itemKeys } = useMemo(() => {
    if (rows.length === 0) {
      return {
        items: [
          <p key="empty" className="ob-count-print-empty">
            No not-recommended crew for these filters.
          </p>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: [
        ...rows.map((row) => (
          <LineRow key={row.key} row={row} showUnknown={showUnknown} />
        )),
        <TotalsRow key="totals" rows={rows} showUnknown={showUnknown} />,
      ],
      itemKeys: [...rows.map((row) => row.key), 'totals'],
    };
  }, [rows, showUnknown]);

  return (
    <ReportFlowPages
      className="ob-count-print"
      size="landscape"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default NrCountReport;
